import { ChangeEvent, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import SubmittedApp from '../../../components/AppCart/GenericApps/Submitted/Submitted';
import Input from '../../../components/UI/Input/Input';
import * as actions from '../../../store/apps/actions';
import {
	checkValidityRules,
	getComponentByComponentId,
	generateMemberKey,
	getMemberFromMembers,
	getFormIdByAppId,
	setSession,
} from '../../../store/utility';

/**
 * AppForm component represents the application request form
 * note: this component was previously known as `Simple`
 * @param {object} props - properties passed to the AppForm component
 */
const AppForm = props => {
	/**
	 * extract variables from props
	 * - appId - T_Application_Master FI_ID
	 * - formId - tab index representing the form (zero-indexed)
	 */
	const { appId, formId } = props;
	/**
	 * component mapping table where the key is the parent component ID and the value is a list of child component IDs
	 */
	const componentMap = useSelector(
		state => state.apps.allForms[formId].model.componentMap
	);

	/**
	 * state object representing the data for the application request forms
	 */
	const appModels = useSelector(state => state.apps.allForms);

	/**
	 * state object representing the application-level rules to be run
	 */
	const rulesReverseLookup = useSelector(
		state => state.apps.rulesReverseLookup
	);

	/**
	 * state object representing the entire application form state
	 */
	const appsData = useSelector(state => state.apps.appsData);

	/**
	 * tracking number for the current request
	 */
	const trackingNumber = useSelector(state => state.apps.trackingNumber);

	/**
	 * action dispatcher
	 */
	const dispatch = useDispatch();

	/**
	 * (set)PayloadReceived - local state indicates when API payload is received
	 */
	const [payloadReceived, setPayloadReceived] = useState(false);

	/**
	 * set tracking number to session if and only if it is AD Groups
	 */
	useEffect(() => {
		let active = true;

		async function setADGroupTrackingNumber() {
			if (appId === 158) {
				await setSession('AD_GROUP_TRACKING_NUMBER', trackingNumber);
			}
		}
		if (active) {
			setADGroupTrackingNumber();
		}
		return () => {
			active = false;
		};
	}, [trackingNumber, appId]);

	/**
	 * useEffect lifecycle hook
	 * page initialization:  check all component members and update validation, API population, and visibility
	 * triggers on updates to appModels[formId]
	 */
	useEffect(() => {
		if (
			!payloadReceived &&
			appModels[formId] &&
			appModels[formId].model &&
			appModels[formId].model.members
		) {
			setPayloadReceived(true);
			const members = appModels[formId].model.members;
			Object.keys(members).forEach(memberId => {
				const member = members[memberId];
				const memberName = generateMemberKey(member);
				const init = true;
				if (member.visible) {
					//unhide child components, call API populate, check validity
					updateMemberValidation(member, memberName, init)
						.then(() => {
							updateMembersApiPopulation(member, memberName).then(
								() => {
									updateMembersVisibility(
										member,
										memberName
									).then(() => {
										invokeAppRules();
									});
								}
							);
						})
						.catch(error => {
							//TODO
						});
				} else {
					//some members come back from API with unexpected validity results
					updateMemberValidation(member, memberName, init);
				}
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [appModels[formId]]);

	/**
	 * change handler for container components
	 * @param {ChangeEvent<HTMLInputElement>} event - triggering HTML event
	 * @param {string} existingValues - component value
	 * @param {boolean} init - (optional) flag indicating first time check
	 */
	const handleContainerChange = (event, existingValues, init = false) => {
		const changedInput = event.target;
		const members = appModels[formId].model.members;
		const member = members[event.target.name.toLowerCase()];
		member.value = existingValues;
		const validity_results = checkValidityRules(member);
		let is_valid = validity_results.valid;
		let message = validity_results.message;
		let validityData = {
			key: changedInput.name,
			value: member.value,
			formId: formId,
			memberId: changedInput.name.toLowerCase(),
			memberValid: is_valid,
			touched: !init,
			validationMessage: message,
			visible: member.visible,
		};

		dispatch(
			actions.checkValidity({
				id: appModels[formId].id,
				data: validityData,
			})
		);
	};

	/**
	 * change handler for form components
	 * @param {ChangeEvent<HTMLInputElement>} event - triggering HTML event
	 * @param {boolean} multi - flag indicating the component contains multiple values concatenated together
	 * @param {boolean} init - (optional) flag indicating first time check
	 */
	const handleChange = (event, multi, init = false) => {
		const changedInput = event.target;
		let existingValues = changedInput.value;
		//account for containers
		if (
			changedInput.elementType &&
			changedInput.elementType === 'containerlist'
		) {
			handleContainerChange(event, existingValues, init);
		} else {
			const memberName = changedInput.name;
			const memberKey = memberName.toLowerCase();
			const members = appModels[formId].model.members;
			const member = members[memberKey];

			//unhide child components, call API validate, call API populate, check validity
			updateMemberValue(member, memberName, multi, changedInput)
				.then(() => {
					updateMemberValidation(member, memberName, init).then(
						() => {
							updateMembersApiPopulation(member, memberName).then(
								() => {
									updateMembersApiValidation(
										member,
										memberName
									).then(() => {
										updateMembersVisibility(
											member,
											memberName
										).then(() => {
											invokeAppRules();
										});
									});
								}
							);
						}
					);
				})
				.catch(error => {
					//TODO
				});
		}
	};

	/**
	 * run the rules for inter-app communication if applicable
	 */
	const invokeAppRules = () => {
		if (rulesReverseLookup[appId]) {
			rulesReverseLookup[appId].forEach(ruleAppId => {
				const targetFormId = getFormIdByAppId(
					{ allForms: appModels },
					ruleAppId
				);
				if (targetFormId && appsData[targetFormId].rules) {
					const payload = {
						rules: appsData[targetFormId].rules,
						allMembers: appModels[targetFormId].model.members,
						watchAllMembers: appModels[formId].model.members,
						trackingNumber: trackingNumber,
					};
					dispatch(actions.runRules(payload));
				}
			});
		}
	};

	/**
	 * update the given member's value
	 * @param {object} member - component member object
	 * @param {boolean} multi - flag indicating the component contains multiple values concatenated together
	 * @param {object} changedInput - represents the `event.target` from the HTML event
	 * @returns {Promise}
	 */
	const updateMemberValue = (member, memberName, multi, changedInput) => {
		return new Promise((resolve, reject) => {
			let newValue = changedInput.value;
			let existingValues = changedInput.value;
			//account for components able to hold multiple values (strings concatenated by '~~')
			if (multi) {
				let existingValuesList = member.value
					? member.value.split('~~')
					: [];
				//check for selection
				if (changedInput.checked) {
					//add if not already there
					if (!existingValuesList.includes(newValue)) {
						existingValuesList.push(newValue);
					}
				} else {
					//remove all instances of value
					for (let i = 0; i < existingValuesList.length; i++) {
						if (existingValuesList[i] === newValue) {
							existingValuesList.splice(i, 1);
						}
					}
				}
				//put the split value back together again
				existingValues = existingValuesList.join('~~');
			}
			member.value = existingValues;

			//update state
			dispatch(
				actions.updateMemberValue({
					member: member,
					memberName: memberName,
					formId: formId,
				})
			);
			resolve();
		});
	};

	/**
	 * check and update the given member's validation based on the rules
	 * @param {object} member - component member object
	 * @param {boolean} init - flag indicating first time check
	 * @returns {Promise}
	 */
	const updateMemberValidation = (member, memberName, init) => {
		return new Promise((resolve, reject) => {
			if (!init) {
				member.validation.touched = true;
			}
			const validityResults = checkValidityRules(member);

			dispatch(
				actions.updateMemberValidity({
					validityData: validityResults,
					member: member,
					memberName: memberName,
					formId: formId,
					touched: !init,
				})
			);
			resolve();
		});
	};

	/**
	 * utility function to get the member name and identifier for a regular or container component member
	 * @param {object} member - component member object
	 * @param {string} memberName - derived member key
	 * @returns {object} - {memberName, componentMapIndex}
	 */
	const determineMemberDetails = (member, memberName) => {
		const members = appModels[formId].model.members;
		//check for containerized component
		let actualId = memberName.split('_')[0];
		let containerizedId = actualId;
		let containerizedMapIndex = member.id;
		if (memberName.split('__').length > 1) {
			containerizedMapIndex = memberName.split('__')[1]; //TODO: this may need to be converted to number
			containerizedId = containerizedMapIndex.split('_')[0];
		}

		const componentMapIndex = containerizedMapIndex;
		//get the member
		Object.keys(members).every(key => {
			if (
				parseInt(key.split('_')[0], 10) === member &&
				members[key].id === containerizedId
			) {
				memberName = key;
				return false; //this return is for breaking out of the `every` loop
			}
			return true; //this return is for breaking out of the `every` loop
		});

		return { memberName: memberName, componentMapIndex: componentMapIndex };
	};

	/**
	 * utility function to perform recursive actions on a member and the member's children
	 * @param {object} member - component member object
	 * @param {string} memberName - derived member key
	 * @param {function} action - function pointer for the action to be performed
	 */
	const updateMembersAction = (member, memberName, action) => {
		const members = appModels[formId].model.members;

		const memberDetails = determineMemberDetails(member, memberName);
		memberName = memberDetails.memberName;
		const componentMapIndex = memberDetails.componentMapIndex;

		//call provided action for this member
		action(member, memberName);

		//call again for all children (recursively)
		if (componentMapIndex) {
			let childrenIds = componentMap[componentMapIndex]
				? componentMap[componentMapIndex]
				: [];
			if (childrenIds) {
				while (childrenIds.length > 0) {
					const childId = childrenIds[0];
					const childMember = getMemberFromMembers(members, childId);
					const childName = generateMemberKey(childMember);
					action(childMember, childName);
					childrenIds = childrenIds.concat(
						componentMap[childId] ? componentMap[childId] : []
					); //add any children of our child (grandchildren) to our queue
					childrenIds.splice(0, 1); //remove the first child in our queue
				}
			}
		}
	};

	/**
	 * checks and updates member's server-side validation
	 * same is done for all children (recursively)
	 * @param {object} member - component member object
	 * @param {string} memberName - derived member key
	 * @returns {Promise}
	 */
	const updateMembersApiValidation = (member, memberName) => {
		return new Promise((resolve, reject) => {
			updateMembersAction(member, memberName, updateMemberApiValidation);
			resolve();
		});
	};

	/**
	 * dispatch call to API validation check for a given member
	 * @param {string} memberName - derived member key
	 * @returns {Promise}
	 */
	const updateMemberApiValidation = (_, memberName) => {
		return new Promise((resolve, reject) => {
			//determine if call to API is appropriate
			if (
				memberName &&
				appModels[formId].model.members[memberName] &&
				appModels[formId].model.members[memberName].validation
					.validationRoute &&
				appModels[formId].model.members[memberName].visible
			) {
				dispatch(
					actions.callApiValidate({
						memberToValidateKey: memberName,
						formToValidateKey: formId,
						allMembers: appModels[formId].model.members,
						componentMap: componentMap,
						trackingNumber: trackingNumber,
					})
				);
			}
			resolve();
		});
	};

	/**
	 * checks and updates member's server-side population actions
	 * same is done for all children (recursively)
	 * @param {object} member - component member object
	 * @param {string} memberName - derived member key
	 * @returns {Promise}
	 */
	const updateMembersApiPopulation = (member, memberName) => {
		return new Promise((resolve, reject) => {
			updateMembersAction(member, memberName, updateMemberApiPopulation);
			resolve();
		});
	};

	/**
	 * dispatch call to API validation check for a given member
	 * @param {string} memberName - derived member key
	 * @param {boolean} forceChildrenHidden - flag indicating server-side population 'show' actions should be overridden for hiding the resulting target
	 * @returns {Promise}
	 */
	const updateMemberApiPopulation = (
		_,
		memberName,
		forceChildrenHidden = false
	) => {
		return new Promise((resolve, reject) => {
			//determine if call to API is appropriate
			if (
				memberName &&
				appModels[formId].model.members[memberName] &&
				appModels[formId].model.members[memberName].populationRoute &&
				(appModels[formId].model.members[memberName].visible ||
					forceChildrenHidden)
			) {
				dispatch(
					actions.callApiPopulate({
						watchComponentKey: memberName,
						formToPopulateKey: formId,
						allMembers: appModels[formId].model.members,
						trackingNumber: trackingNumber,
						forceChildrenHidden: forceChildrenHidden,
					})
				);
			}
			resolve();
		});
	};

	/**
	 * determines whether a given child should be made visible for a given parent
	 * @param {object} childMember - child component member object
	 * @param {object} parentMember - parent component member object
	 * @returns {boolean}
	 */
	const showIfConditionsMet = (childMember, parentMember) => {
		let show = false;
		const exemptFromVisibilityRules = ['container'];
		if (parentMember.visible) {
			if (
				childMember &&
				!exemptFromVisibilityRules.includes(childMember.elementType)
			) {
				const targetOptionIds = childMember.showIfOptionSelected;
				if (targetOptionIds && targetOptionIds.length > 0) {
					if (
						parentMember &&
						parentMember.elementConfig &&
						parentMember.elementConfig.options
					) {
						const options = parentMember.elementConfig.options;
						targetOptionIds.every(targetOptionId => {
							Object.keys(options).every(optionId => {
								if (options[optionId].ApplicationData) {
									options[optionId].ApplicationData.every(
										option => {
											const parentMemberValues =
												parentMember.value
													? parentMember.value.split(
															'~~'
													  )
													: [];
											if (
												parentMemberValues.includes(
													option.Value
												) &&
												option.Key ===
													targetOptionId.toString()
											) {
												show = true;
											}
											return !show; //this return is for breaking out of the `every` loop
										}
									);
								}
								return !show; //this return is for breaking out of the `every` loop
							});
							return !show; //this return is for breaking out of the `every` loop
						});
					}
				} else {
					if (parentMember.value) {
						show = true;
					}
				}
			}
		}
		return show;
	};

	/**
	 * checks and updates member's visibility
	 * same is done for all children (recursively)
	 * @param {object} member - component member object
	 * @param {string} memberName derived member key
	 * @returns {Promise}
	 */
	const updateMembersVisibility = (member, memberName, _) => {
		return new Promise((resolve, reject) => {
			updateMembersAction(member, memberName, updateMemberVisibility);
			resolve();
		});
	};

	/**
	 * dispatch visibility update for a given member and its children (recursively)
	 * @param {object} member - component member object
	 * @returns {Promise}
	 */
	const updateMemberVisibility = (member, _) => {
		return new Promise((resolve, reject) => {
			let childIds = componentMap[member.id]
				? componentMap[member.id]
				: [];
			const members = appModels[formId].model.members;

			while (childIds.length > 0) {
				const childId = childIds[0];
				const childMember = getComponentByComponentId(childId, members);

				if (childMember) {
					const newVisibility = showIfConditionsMet(
						childMember,
						member
					);
					dispatch(
						actions.updateMemberVisibility({
							member: childMember,
							formId: formId,
							visibility: newVisibility,
						})
					);

					const childMemberName = generateMemberKey(childMember);
					if (newVisibility) {
						//call API Populate for newly shown child
						updateMemberApiPopulation(
							childMember,
							childMemberName,
							false
						);
						//call API Validate for newly shown child
						updateMemberApiValidation(childMember, childMemberName);
					} else {
						updateMemberApiPopulation(
							childMember,
							childMemberName,
							true
						);
					}
					childIds = childIds.concat(
						componentMap[childId] ? componentMap[childId] : []
					); //add any children of our child (grandchildren) to our queue
				}
				childIds.splice(0, 1); //remove the first child in our queue
			}

			resolve();
		});
	};

	/**
	 * determines if the component type takes a value that can be concatenated together with other values
	 * @param {string} elementType - F_Full from T_Component_Type
	 * @returns {boolean} - true if element type accepts multi-values
	 */
	const isMultiInput = elementType => {
		const multiElements = ['checkbox', 'checkboxlist', 'multiselect'];
		return multiElements.includes(elementType);
	};

	/**
	 * boolean; indicates when the form has been submitted
	 */
	const formSubmitted = appModels ? appModels[formId].submitted : false;

	/**
	 * JSX placeholder for a submitted app
	 */
	const formSubmittedOutput = <SubmittedApp />;

	/**
	 * JSX placeholder for the app form
	 */
	const form = appModels ? (
		<>
			<div>
				{appModels[formId].roleDescriptionLink ? (
					<Link
						id={`app-${appId}__role-descriptions`}
						to={() => {
							try {
								return {
									pathname: new URL(
										appModels[formId].roleDescriptionLink
									).toString(),
								};
							} catch {
								return appModels[formId].roleDescriptionLink;
							}
						}}
						target='_blank'
					>
						Role Descriptions
					</Link>
				) : null}
			</div>
			<div>
				<div className='form-group'>
					{Object.keys(appModels[formId].model.members).map(
						(key, index) => {
							const formElement =
								appModels[formId].model.members[key];
							let multi = false;
							if (isMultiInput(formElement.elementType)) {
								multi = true;
							}
							const x = (
								<Input
									formElId={formElement.id}
									appId={appId}
									elementType={formElement.elementType}
									elementConfig={formElement.elementConfig}
									key={key}
									value={formElement.value}
									label={formElement.label}
									sublabel={formElement.sublabel}
									name={key}
									visible={
										appModels[formId].model.members[key]
											.visible
									}
									changeHandler={event =>
										handleChange(event, multi, false)
									}
									styleClasses={
										formElement.styleClasses
											? formElement.styleClasses
											: []
									}
									validation={formElement.validation}
									refs={
										appModels[formId].name +
										':' +
										formElement.label
									}
									extras={formElement.extras}
								/>
							);
							return x;
						}
					)}
				</div>
			</div>
		</>
	) : null;

	/**
	 * JSX placeholder for AppForm's returned rendering
	 */
	const output = formSubmitted ? formSubmittedOutput : form;

	/**
	 * returned JSX
	 */
	return output;
};

export default AppForm;
